import {
  HStack,
  Text,
  ListItem,
  UnorderedList,
  VStack,
  Box,
  Spacer,
  Avatar,
  IconButton,
} from "@chakra-ui/react";
import dayjs, { Dayjs } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import {
  KYDEventScheduledUpdateSummary,
  KYDPerformerDisplay,
} from "@common/interfaces/KYDEvent";
import { labelToText } from "./steps/ReviewStep";
import { FiClock, FiTrash2 } from "react-icons/fi";

dayjs.extend(advancedFormat);

const formatDate = (date: Dayjs) => {
  return date.format("MMM D h:mmA");
};

export const formatFullDate = (date: Dayjs, tz?: string) => {
  // Ensure timezone plugin is loaded
  if (tz) {
    date = date.tz(tz);
  }
  // Use \u2018 for left single quotation mark
  return date.format("MMM D \u2018YY, dddd   hh:mmA z");
};

function isPerformerArray(
  value: string | KYDPerformerDisplay[]
): value is KYDPerformerDisplay[] {
  return Array.isArray(value);
}

const UpdateItem = ({
  label,
  value,
  field,
  onEdit,
  timezone,
}: {
  label: string;
  value: string | KYDPerformerDisplay[];
  field: "name" | "description" | "date" | "poster" | "performers";
  onEdit: () => void;
  timezone?: string;
}) => {
  return (
    <ListItem
      w="full"
      borderBottomWidth={1}
      borderColor={"gray.200"}
      listStyleType="none"
      display="flex"
      alignItems="center"
      py={4}
      px={4}
    >
      <Box
        as="span"
        display="inline-block"
        w="8px"
        h="8px"
        bg="gray.400"
        borderRadius="full"
        flexShrink={0}
        mr={3}
      />
      <HStack w="full" justify={"flex-start"} gap={8}>
        <Text flexShrink={0} fontWeight={"medium"}>
          {labelToText(label)}
        </Text>
        <Text
          onClick={() => {
            onEdit();
          }}
          flexShrink={0}
          color="#1D74FF"
          fontSize={"sm"}
          fontWeight={"medium"}
          cursor={"pointer"}
        >
          Edit / View
        </Text>
        <HStack
          gap={4}
          flexShrink={0}
          flexGrow={1}
          overflowX="auto"
          sx={{
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE 10+
            "&::-webkit-scrollbar": {
              display: "none", // Chrome/Safari/Webkit
            },
          }}
        >
          {field === "performers" &&
            isPerformerArray(value) &&
            value.map((performer: KYDPerformerDisplay) => (
              <HStack alignItems={"center"} gap={1}>
                <Avatar size="sm" src={performer.image_url} />
                <VStack w="full" alignItems={"flex-start"} gap={0}>
                  <Text fontSize={"sm"} fontWeight={"bold"}>
                    {performer.name}
                  </Text>
                  <HStack gap={1}>
                    <FiClock />
                    <Text fontSize={"xs"} color="gray.500">
                      Set time:{" "}
                      {performer.set_time
                        ? dayjs(performer.set_time)
                            .tz(timezone)
                            .format("hh:mm A")
                        : "Not set"}
                    </Text>
                  </HStack>
                </VStack>
              </HStack>
            ))}
        </HStack>
      </HStack>
    </ListItem>
  );
};

export const ScheduledUpdatesTable = ({
  onEdit,
  loading,
  readOnly = false,
  timezone,
  isDisabled = false,
  scheduledUpdates = [],
  onDelete,
  ...props
}: {
  onEdit: (
    update: KYDEventScheduledUpdateSummary,
    field: "name" | "description" | "date" | "poster" | "performers"
  ) => void;
  onDelete: (scheduledUpdate: KYDEventScheduledUpdateSummary) => void;
  readOnly: boolean;
  timezone?: string;
  isDisabled?: boolean;
  loading: any;
  scheduledUpdates: KYDEventScheduledUpdateSummary[];
}) => {
  return (
    <VStack w="full">
      <UnorderedList w="full" spacing={0} margin={0}>
        {scheduledUpdates.map((update) => (
          <ListItem
            key={update.id}
            w="full"
            flexShrink={0}
            listStyleType="none"
            margin={0}
            py={4}
          >
            <HStack w="full" justify={"space-between"}>
              <Text
                fontWeight={"medium"}
                textTransform={"uppercase"}
                color="gray.500"
              >
                {formatFullDate(dayjs(update.date), timezone)}
              </Text>
              <IconButton
                icon={<FiTrash2 />}
                aria-label="Delete"
                onClick={() => {
                  onDelete(update);
                }}
              />
            </HStack>
            <VStack w="full" spacing={0} margin={0}>
              <UnorderedList w="full" spacing={0} margin={0}>
                {Array.isArray(update.performers) &&
                  update.performers.length > 0 && (
                    <UpdateItem
                      timezone={timezone}
                      label="Performers"
                      field="performers"
                      value={update.performers}
                      onEdit={() => {
                        onEdit(update, "performers");
                      }}
                    />
                  )}
                {update.name && (
                  <UpdateItem
                    label="Name"
                    field="name"
                    value={update.name}
                    onEdit={() => {
                      onEdit(update, "name");
                    }}
                  />
                )}
                {update.description && (
                  <UpdateItem
                    label="Description"
                    field="description"
                    value={update.description}
                    onEdit={() => {
                      onEdit(update, "description");
                    }}
                  />
                )}
                {update.poster && (
                  <UpdateItem
                    label="Poster"
                    field="poster"
                    value={update.poster}
                    onEdit={() => {
                      onEdit(update, "poster");
                    }}
                  />
                )}
              </UnorderedList>
            </VStack>
          </ListItem>
        ))}
        {scheduledUpdates.length === 0 && (
          <VStack w="full" py={4} align={"center"}>
            <Text>No scheduled updates. Create one to get started.</Text>
          </VStack>
        )}
      </UnorderedList>
    </VStack>
  );
};
